<template>
  <div ref="root" class="adaptive-card-root"></div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue'
import { AdaptiveCard, HostConfig } from 'adaptivecards'
import { Template as ACTemplate } from 'adaptivecards-templating'
import 'adaptivecards/dist/adaptivecards.css'
// If you plan to use Carousel elements, also import:
// import 'adaptivecards/dist/adaptivecards-carousel.css'

const props = defineProps({
  /** Adaptive Card JSON template (object or JSON string). Can include ${} bindings. */
  template: { type: [Object, String], required: true },
  /** Data for template expansion (bound to $root). */
  data: { type: Object, default: null },
  /** Optional HostConfig JSON to control styling/behavior. */
  hostConfig: { type: Object, default: null },
  /** When true, log detailed info & warnings to the console. */
  debug: { type: Boolean, default: false }
})

const emit = defineEmits([
  'action',        // { type, data, url, action, inputs }
  'submit',        // payload for Action.Submit
  'execute',       // payload for Action.Execute
  'open-url',      // { url, data }
  'rendered',
  'error'
])

const root = ref(null)

function log(...args) {
  if (props.debug) console.log('[AdaptiveCard]', ...args)
}
function warn(...args) {
  if (props.debug) console.warn('[AdaptiveCard]', ...args)
}
function errlog(e) {
  console.error('[AdaptiveCard] render error', e)
}

function asJson(input) {
  if (typeof input === 'string') return JSON.parse(input)
  return input
}

function renderCard() {
  if (!root.value) return
  // Clear previous content
  root.value.innerHTML = ''

  try {
    const templateJson = asJson(props.template)
    log('template', templateJson)

    // If data was provided, expand the template; otherwise treat it as a plain card
    let cardPayload = templateJson
    if (props.data !== undefined) {
      const tpl = new ACTemplate(templateJson)
      cardPayload = tpl.expand({ $root: props.data || {} })
      log('expanded payload', cardPayload)
    }

    const ac = new AdaptiveCard()

    // Surface parse warnings/errors if available
    ac.onParseError = (sender, error) => warn('parse error', error?.message || error)
    ac.onParseWarning = (sender, warning) => warn('parse warning', warning?.message || warning)

    if (props.hostConfig) {
      ac.hostConfig = new HostConfig(props.hostConfig)
      log('hostConfig applied')
    }

    // Forward all action executions to the parent as Vue events
    ac.onExecuteAction = action => {
      const type = typeof action.getJsonTypeName === 'function'
          ? action.getJsonTypeName()
          : (action.type || (action.constructor && action.constructor.name))

      const payload = { type, data: action.data, url: action.url, action }
      try {
        const inputs = {}
        ac.getAllInputs().forEach(input => { inputs[input.id] = input.value })
        payload.inputs = inputs
      } catch {
        console.error('[AdaptiveCard] executeAction data mapping error', action)
      }

      emit('action', payload)
      const t = String(type)
      if (t.includes('Action.Submit') || t.includes('SubmitAction')) emit('submit', payload)
      else if (t.includes('Action.Execute') || t.includes('ExecuteAction')) emit('execute', payload)
      else if (t.includes('Action.OpenUrl') || t.includes('OpenUrlAction')) emit('open-url', { url: payload.url, data: payload.data })
    }

    ac.parse(cardPayload)
    const rendered = ac.render()
    log('rendered element', rendered)
    root.value.appendChild(rendered)
    emit('rendered')
  } catch (err) {
    errlog(err)
    // Show a minimal inline error so the user knows something failed
    const pre = document.createElement('pre')
    pre.textContent = `[AdaptiveCard error] ${err?.message || err}`
    pre.style.whiteSpace = 'pre-wrap'
    pre.style.color = 'crimson'
    root.value.appendChild(pre)
    emit('error', err)
  }
}

watch(() => [props.template, props.data, props.hostConfig], () => renderCard(), { deep: true })

onMounted(async () => {
  await nextTick()
  renderCard()
})

onBeforeUnmount(() => {
  if (root.value) root.value.replaceChildren()
})

// defineExpose({ rerender: renderCard })
</script>

<!-- AdaptiveCard.vue -->
<style scoped>
/* Inputs */
.adaptive-card-root :deep(.ac-input),
.adaptive-card-root :deep(input.ac-input),
.adaptive-card-root :deep(textarea.ac-input),
.adaptive-card-root :deep(select.ac-input) {
  background: #ffffff;
  color: #333333;
  border: 1px solid #d1d1d1;
  border-radius: 4px;
  padding: 6px 12px;
  height: 36px;
  box-sizing: border-box;
}

/* Button styling */
.adaptive-card-root :deep(.ac-pushButton):hover {
  background-color: #e21833;
  color: white;
}

.adaptive-card-root :deep(.ac-pushButton.expandable)::after {
  content: " ▶" !important;
  margin-left: 4px;
}
.adaptive-card-root :deep(.ac-pushButton.expandable.expanded) {
  background-color: rgb(69, 69, 69) !important;
}
.adaptive-card-root :deep(.ac-pushButton.expandable.expanded)::after {
  content: " ▼" !important;
  margin-left: 4px;
}
</style>

